import React from 'react';
import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { validateHost, validatePort } from '../printing/printersApi';

/**
 * Adding a station printer, and editing one.
 *
 * One dialog for both, as on the phone — the fields are identical, and
 * keeping them together means a field added later can never be forgotten on
 * the other form. Its wording, and its validation messages, verbatim.
 *
 * The Active switch appears only when editing: a printer is created switched
 * on, and the server defaults it that way whether or not the field is sent.
 */

const COPY = {
  addTitle: 'New Printer',
  editTitle: 'Edit Printer',
  name: 'Name (example Kitchen)',
  host: 'IP address (192.168.18.60)',
  port: 'Port',
  active: 'Active',
  activeNote: 'When switched off, categories pointing here print no ticket',
  nameRequired: 'Enter a printer name',
  hostInvalid: 'Not a valid IP address (example 192.168.18.60)',
  portInvalid: 'Not a valid port (usually 9100)',
  add: 'Add Printer',
  save: 'Save',
  failed: 'Could not save. Try again.',
};

/** A switch, because a checkbox reads as a form field and this is a state. */
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        position: 'relative',
        width: 42,
        height: 24,
        padding: 0,
        background: checked ? T.brand : T.borderStrong,
        border: 'none',
        borderRadius: T.radiusPill,
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        transition: 'background 120ms linear',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: checked ? 21 : 3,
          width: 18,
          height: 18,
          background: T.surface,
          borderRadius: T.radiusPill,
          transition: 'left 120ms ease',
        }}
      />
    </button>
  );
}

export default function PrinterDialog({ printer, businessId, onClose, onSubmit }) {
  const editing = Boolean(printer);

  const [name, setName] = React.useState(() => (printer && printer.name) || '');
  const [host, setHost] = React.useState(() => (printer && printer.host) || '');
  const [port, setPort] = React.useState(() =>
    printer && printer.port ? String(printer.port) : '9100'
  );
  const [active, setActive] = React.useState(() => !printer || printer.isActive !== false);

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;

    if (!name.trim()) {
      setError(COPY.nameRequired);
      return;
    }
    if (!validateHost(host)) {
      setError(COPY.hostInvalid);
      return;
    }
    if (!validatePort(port)) {
      setError(COPY.portInvalid);
      return;
    }

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        printer,
        payload: editing
          ? { name: name.trim(), host: host.trim(), port: Number(port), isActive: active }
          : { name: name.trim(), host: host.trim(), port: Number(port), business: businessId },
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={410}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.editTitle : COPY.addTitle}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 18 }}>
          <PillField
            name="printer-name"
            value={name}
            onChange={(value) => {
              setName(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.name}
            disabled={busy}
            autoFocus
          />

          <PillField
            name="printer-host"
            value={host}
            onChange={(value) => {
              setHost(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.host}
            disabled={busy}
          />

          <PillField
            name="printer-port"
            type="number"
            value={port}
            onChange={(value) => {
              setPort(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.port}
            disabled={busy}
          />

          {editing ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 16px',
                background: T.sunk,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusControl,
              }}
            >
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{COPY.active}</span>
                <span style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.45 }}>
                  {COPY.activeNote}
                </span>
              </div>
              <Toggle checked={active} onChange={setActive} disabled={busy} />
            </div>
          ) : null}
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? COPY.save : COPY.add}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
